import { deleteRequest, post } from 'helpers/api';
import { useState, useEffect } from 'react';
import { withPreviewEye } from './preview_eye';

import BasicCard from './basic_card';
import CompanyCard from './company_card';
import SyndicationCard from './syndication_card';
import LeadsCard from './leads_card';
import ReferralSourceCard from './referral_source_card';
import EmailAnalytics from './email_analytics_card';
import EmailDetails from './email_details_card';
import WebsiteTraffic from './website_traffic';
import ActivitySummaryCard from './activity_summary_card';
import ActivityFunnel from './activity_funnel';
import CustomCard from './custom_card';
import DocumentStatsCard from './document_stats_card';

export const BasicCardWithPreview = withPreviewEye(BasicCard);
export const CompanyCardWithPreview = withPreviewEye(CompanyCard);
export const SyndicationCardWithPreview = withPreviewEye(SyndicationCard);
export const LeadsCardWithPreview = withPreviewEye(LeadsCard);
export const ReferralSourceCardWithPreview = withPreviewEye(ReferralSourceCard);
export const EmailAnalyticsWithPreview = withPreviewEye(EmailAnalytics);
export const EmailDetailsWithPreview = withPreviewEye(EmailDetails);
export const WebsiteTrafficWithPreview = withPreviewEye(WebsiteTraffic);
export const ActivitySummaryCardWithPreview = withPreviewEye(ActivitySummaryCard);
export const ActivityFunnelWithPreview = withPreviewEye(ActivityFunnel);
export const CustomCardWithPreview = withPreviewEye(CustomCard);
export const DocumentStatsCardWithPreview = withPreviewEye(DocumentStatsCard);

// Styles all cards that aren't in the top row
export const NonBasicCardStyling = 'ds-client-report-card d-flex flex-column gap-4 p-4 h-100';

export function shareClientReport(projectId, collaboratorId, email, message) {
  const params = {
    collaborator_id: collaboratorId,
    email: email,
    message: message
  };
  return post(`/projects/${projectId}/shareable_tokens/share`, params);
}

export function removeShareableToken(projectId, shareableTokenId) {
  return deleteRequest(`/projects/${projectId}/shareable_tokens/${shareableTokenId}`);
}

export function formatDateTime(dateString: string) {
  const date = new Date(dateString);
  return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
}

export type Card = {
  cardType: string,
  displayCard: boolean,
  filters: {},
  cardId?: number
}

export const initialDefaultCards: Card[] = [
  {
    cardType: 'totalDaysOnMarket',
    displayCard: true,
    filters: {}
  },
  {
    cardType: 'totalCasSigned',
    displayCard: true,
    filters: {}
  },
  {
    cardType: 'totalLeads',
    displayCard: true,
    filters: {}
  },
  {
    cardType: 'activitySummary',
    displayCard: true,
    filters: {}
  },
  {
    cardType: 'activityFunnel',
    displayCard: true,
    filters: {}
  },
  {
    cardType: 'companyCaSummary',
    displayCard: true,
    filters: {}
  },
  {
    cardType: 'leads',
    displayCard: true,
    filters: {}
  },
  {
    cardType: 'emailAnalytics',
    displayCard: true,
    filters: {}
  },
  {
    cardType: 'websiteTraffic',
    displayCard: true,
    filters: {}
  },
  {
    cardType: 'referralSources',
    displayCard: true,
    filters: {}
  },
  {
    cardType: 'syndication',
    displayCard: true,
    filters: {}
  },
  {
    cardType: 'documentStats',
    displayCard: true,
    filters: {}
  }
];

export type ShareableToken = {
  id: number;
  email: string;
  enabled: boolean;
  lastSent: string | null;
  name: string;
  relationship: string;
  contact: ShareableTokenContact;
}

export type ShareableTokenContact = {
  id: number;
  lastSent: string | null;
}

export type Collaborator = {
  id: number;
  email: string;
  phone: string;
  imageUrl: string;
  name: string;
  role: string;
  removeable: boolean;
  jobTitle: string;
  notificationsBlocked: boolean;
}

// Utility that determines when to shrink from 3 cards per row to 1 card per row
type WindowSize = {
  width: number | undefined;
  height: number | undefined;
};

export function useWindowSize(): WindowSize {
  const [windowSize, setWindowSize] = useState<WindowSize>({
    width: undefined,
    height: undefined,
  });

  useEffect(() => {
    function handleResize() {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    }

    window.addEventListener('resize', handleResize);
    handleResize(); // Call handler right away so state gets updated with initial window size

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return windowSize;
}
